import React, { useEffect, useRef, useState } from "react";
import { format } from "date-fns";
import { toast } from "react-toastify";

import TemplateTable from "../../components/TemplateTable";
import { ITableAppRender } from "../../components/TableApp";
import { INotificacaoListModel } from "../../Repositories/NotificationRepository";
import { IFields } from "../../components/Search/FilterBuilder";
import { ISearch } from "../../components/Search";
import errors from "../../Utils/ExtractErrors";
import { ISearchResponse } from "../../interfaces";
import NotificationRepository from "../../Repositories/NotificationRepository";
import { Button, Label } from "semantic-ui-react";
import UserVisualizarNotificacao from "./UserVisualizarNotificacao";
import ModalYesNo from "../../components/ModalYesNo";

interface IState {
    loading: boolean;
    list: INotificacaoListModel[];
}

const initialState: IState = {
    loading: true,
    list: []
}

export default function UserNotifications() {
    const [state, setState] = useState<IState>(initialState);

    const refSearch = useRef<ISearch>({ searchString: "", type: "simples" });
    const refRecordsByPage = useRef<number>(20);
    const refPageNumber = useRef<string | number | undefined>(1);
    const refTotalRecords = useRef<number>(0);
    const refTotalPages = useRef<number>(1);

    useEffect(() => {
        load();
    }, []);

    async function load() {
        setState(state => ({ ...state, loading: true }));
        try {
            let response: ISearchResponse<INotificacaoListModel>;

            if (refSearch.current.type === "simples") {
                response = await NotificationRepository.List({
                    search: refSearch.current.searchString,
                    pageNumber: refPageNumber.current,
                    recordsByPage: refRecordsByPage.current
                });
            } else {
                response = await NotificationRepository.List({
                    filter: refSearch.current.searchString,
                    pageNumber: refPageNumber.current,
                    recordsByPage: refRecordsByPage.current
                });
            }

            refTotalRecords.current = response.totalRecords;
            refTotalPages.current = response.totalPages;

            setState({ loading: false, list: response.list });
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });

            setState(state => ({ ...state, loading: false }));
        }
    }

    function onSearch({ searchString, type }: ISearch) {
        refSearch.current.searchString = searchString;
        refSearch.current.type = type;
        load();
    }

    function onChangeRecords(value: number) {
        refRecordsByPage.current = value;
        load();
    }

    function onPageChange(pageNumber: string | number | undefined) {
        refPageNumber.current = pageNumber;
        load();
    }

    function handleLabelVisualizada(visualizada: boolean) {
        if (visualizada) {
            return <Label
                ribbon
                icon="check"
                color="pink"
                content="VIS"
            />;
        } else {
            return <Label
                ribbon
                icon="times"
                color="grey"
                content="NVI"
            />;
        }
    }

    async function handleRemove(id: string) {
        try {
            await NotificationRepository.SetRemoved(id);
            load();
        } catch (err) {
            if (err.response.status === 401) {
                window.location.href = "/";
            }

            const error = errors.getAllErrors(err.response.data);

            if (error !== undefined)
                toast(error, { position: "top-center", type: "error" });
        }
    }

    function handleTableActions(data: INotificacaoListModel, index: number) {
        return ([
            <UserVisualizarNotificacao
                id={data.id}
                title={data.descricao}
                onSuccess={load}
            />,
            <ModalYesNo
                trigger={<Button icon="trash" color="red" />}
                header="Apagar"
                content="Deseja realmente apagar esta notificação?"
                onSuccess={() => handleRemove(data.id)}
            />
        ]);
    }

    const tableRender: ITableAppRender[] = [
        {
            header: "Visualizada",
            content: (notificacao: INotificacaoListModel) => handleLabelVisualizada(notificacao.visualizada),
            width: 2
        },
        {
            header: "Data",
            content: (notificacao: INotificacaoListModel) => format(new Date(notificacao.criadaEm), "dd/MM/yyyy"),
            width: 3
        },
        {
            header: "Descrição",
            content: (notificacao: INotificacaoListModel) => notificacao.descricao,
            width: 9
        }
    ];

    const fields: IFields[] = [
        { label: "Data", name: "CriadaEm", type: "date", roles: ["*"] },
        { label: "Descrição", name: "Descricao", type: "string", roles: ["*"] },
        {
            label: "Visualizada",
            name: "Visualizada",
            type: "enum",
            values: [
                { key: "TRUE", text: "Visualizada", value: true, label: { icon: "check", color: "pink", content: "VIS" } },
                { key: "FALSE", text: "Não visuali.", value: false, label: { icon: "times", color: "grey", content: "NVI" } },
            ],
            roles: ["*"]
        }
    ];

    return (
        <TemplateTable
            title="Notificações"
            icon="bell"
            searchPlaceHolder="Descrição"
            filterFields={fields}
            onSearch={onSearch}
            tableLoading={state.loading}
            tableList={state.list}
            tableRender={tableRender}
            tableActions={handleTableActions}
            tableOnChangeRecords={onChangeRecords}
            tablePagination={{
                currentPage: refPageNumber.current,
                totalPages: refTotalPages.current,
                totalRecords: refTotalRecords.current,
                onPageChange: onPageChange
            }}
        />
    )
}
